import React, { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

export const Header = () => {
  const { t, lang, toggle } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass =
    "text-sm text-foreground/80 hover:text-[#2A5A3B] transition-colors";

  const links = [
    { href: "#services", label: t.nav.services, id: "nav-services-link" },
    { href: "#about", label: t.nav.about, id: "nav-about-link" },
    { href: "#quote", label: t.nav.quote, id: "nav-quote-link" },
    { href: "#contact", label: t.nav.contact, id: "nav-contact-link" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[#E5E0D8]/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="brand-logo"
          className="flex items-center gap-3 hover:opacity-85 transition-opacity"
        >
          <img
            src="/cesars-logo.png"
            alt="Cesar's Repairs"
            className="h-12 w-12 md:h-14 md:w-14 object-contain shrink-0"
          />
          <span className="leading-tight">
            <span className="block font-display text-base font-semibold tracking-tight text-[#1C1C1C]">
              Cesar's Repairs
            </span>
            <span className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Mont-Royal, QC
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className={linkClass}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            type="button"
            onClick={toggle}
            data-testid="language-toggle"
            className="text-xs uppercase tracking-[0.2em] font-semibold text-foreground/70 hover:text-[#2A5A3B] transition-colors border border-[#E5E0D8] rounded-full px-3 py-1.5"
            aria-label="Toggle language"
          >
            {lang === "en" ? "FR" : "EN"}
          </button>
          <a
            href="tel:+14388743855"
            data-testid="phone-cta"
            className="inline-flex items-center gap-2 bg-[#2A5A3B] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#244d32] transition-colors rounded-none"
          >
            <Phone className="h-4 w-4" />
            {t.nav.callNow}
          </a>
        </div>

        <button
          type="button"
          className="lg:hidden p-2 text-foreground"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FDFBF7] border-t border-[#E5E0D8] px-6 py-6 space-y-4">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`${l.id}-mobile`}
              onClick={() => setOpen(false)}
              className="block text-base text-foreground/80 hover:text-[#2A5A3B]"
            >
              {l.label}
            </a>
          ))}
          <div className="flex items-center gap-3 pt-4 border-t border-[#E5E0D8]">
            <button
              type="button"
              onClick={toggle}
              data-testid="language-toggle-mobile"
              className="text-xs uppercase tracking-[0.2em] font-semibold border border-[#E5E0D8] rounded-full px-3 py-1.5"
            >
              {lang === "en" ? "FR" : "EN"}
            </button>
            <a
              href="tel:+14388743855"
              data-testid="phone-cta-mobile"
              className="inline-flex items-center gap-2 bg-[#2A5A3B] text-white px-4 py-2.5 text-sm font-medium"
            >
              <Phone className="h-4 w-4" /> {t.nav.callNow}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
